import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import { ReactComponent as Bookmark } from "../../assets/bookmark.svg";
import { takeLastSearchFilter } from "../../utils/RouteConstants";
import { ConDesignItemLite } from "../../types/ConDesignItem";
import CDIsContainer from "../common/CDIsContainer";
import ConDesignItem from "../common/ConDesignItem";
import SigninModal from "../common/SigninModal";
import Loader from "../common/Loader";
import ShowMoreButton from "../common/ShowMoreButton";
import {
	clearBookmarkRemovedCallback,
	setBookmarkRemovedCallback,
	useFetchDesignItemsLiteAnonymousQuery,
} from "../../services/clApiSlice";
import Filters from "../layout/Filters";
import { useFilters } from "../../hooks/useFilters";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectItemsLite, setDesignItemsLite, setIsLastPage } from "../../services/designItemsSlice";
import { FeatureClauses } from "../../types/CDIFilter";

interface MyBookmarksPageProps {
	isAuthenticated: boolean | undefined;
}

const MyBookmarksPage = ({ isAuthenticated }: MyBookmarksPageProps) => {
	const [selectedItem, setSelectedItem] = useState<ConDesignItemLite | null>(null);
	const [showSigninModal, setShowSigninModal] = useState<boolean>(false);
	const [paginationFrom, setPaginationFrom] = useState<number>(0);
	const [isLoadingShowMore, setIsLoadingShowMore] = useState<boolean>(false);
	
	const location = useLocation();
	const { itemId } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	
	const designItemsLite = useAppSelector(selectItemsLite);
	
	// Reset function for when filters change
	const resetBookmarksData = () => {
		setPaginationFrom(0);
		dispatch(
			setDesignItemsLite({
				data: { designItems: [], pageSize: 0, isLastPage: true, totalItems: "filtering" },
				isShowMoreClicked: false,
			}),
		);
	};
	
	// Use filters hook - exclude bookmark filters from UI since we force my_bookmarks
	const filters = useFilters({
		excludeFilters: ["my_bookmarks", "others_bookmarks"],
		onFilterChange: resetBookmarksData,
	});
	
	const {
		connectorFilters,
		featureFilters,
		popularityFilters,
		designCodeFilter,
		setFeatureFilters,
		setConnectorFilters,
		setPopularityFilters,
		setDesignCodeFilter,
		handleApplyFilters,
		handleApplyPopularityFilter,
		handleSetOnlyFilter,
		clearAllFilters,
	} = filters;
	
	// Build search query - FORCE my_bookmarks to be "must"
	const searchQuery = useMemo(() => {
		const searchQueryString = encodeURIComponent(
			JSON.stringify({
				members: [], // No member filters for bookmarks
				featureFilters: [...connectorFilters, ...featureFilters],
				popularityFilters: {
					MyBookmarks: FeatureClauses.Must, // FORCE must
					OthersBookmarks: FeatureClauses.None,
				},
				designCodeFilter,
			}),
		);
		
		return {
			searchQueryString,
		};
	}, [connectorFilters, featureFilters, designCodeFilter]);
	
	// Fetch design items with forced my_bookmarks filter
	const {
		data: designItemsLiteData,
		error: designItemsLiteError,
		isFetching: isFetchingDesignItemsLite,
	} = useFetchDesignItemsLiteAnonymousQuery(
		searchQuery.searchQueryString
			? { ...searchQuery, paginationFrom }
			: skipToken,
	);
	
	// Update Redux state when data loads
	useEffect(() => {
		if (designItemsLiteData?.designItems) {
			dispatch(
				setDesignItemsLite({
					data: designItemsLiteData,
					isShowMoreClicked: paginationFrom !== 0,
				}),
			);
			dispatch(setIsLastPage(designItemsLiteData.isLastPage));
		}
	}, [designItemsLiteData, paginationFrom, dispatch]);
	
	useEffect(() => {
		if (designItemsLiteError) {
			setPaginationFrom(0);
			dispatch(
				setDesignItemsLite({
					data: { designItems: [], pageSize: 0, isLastPage: true, totalItems: 0 },
					isShowMoreClicked: false,
				}),
			);
		}
	}, [designItemsLiteError, dispatch]);
	
	useEffect(() => {
		if (!isFetchingDesignItemsLite) {
			setIsLoadingShowMore(false);
		}
	}, [isFetchingDesignItemsLite]);
	
	// Remove bookmark item from Redux state
	const removeBookmarkItem = useCallback(
		(itemId: string) => {
			const updatedItems = designItemsLite.designItems.filter(
				(item) => item.id !== itemId,
			);
			dispatch(
				setDesignItemsLite({
					data: {
						designItems: updatedItems,
						pageSize: designItemsLite.pageSize,
						isLastPage: designItemsLite.isLastPage,
						totalItems:
							typeof designItemsLite.totalItems === "number"
								? Math.max(0, designItemsLite.totalItems - 1)
								: designItemsLite.totalItems,
					},
					isShowMoreClicked: false,
				}),
			);
		},
		[designItemsLite, dispatch],
	);
	
	// Set up the global callback for bookmark removal
	useEffect(() => {
		setBookmarkRemovedCallback(removeBookmarkItem);
		
		return () => {
			clearBookmarkRemovedCallback();
		};
	}, [removeBookmarkItem]);
	
	useEffect(() => {
		window.dataLayer.push({
			event: "cl_my_bookmarks_page_visited",
		});
	}, []);
	
	// Get the redirect URI from URL params
	const urlParams = new URLSearchParams(location.search);
	const backRedirectUri = urlParams.get("redirectUri");
	
	const getBreadcrumbText = () => {
		if (!backRedirectUri) {
			return <FormattedMessage id="BackToSearch" defaultMessage="Back to search" />;
		}
		
		const decodedUri = decodeURIComponent(backRedirectUri);
		if (decodedUri.includes("/set/public"))
			return <FormattedMessage id="BackToResults" defaultMessage="Back to results" />;
		if (decodedUri === "/" || decodedUri.startsWith("/?"))
			return <FormattedMessage id="BackToSearch" defaultMessage="Back to search" />;
		return <FormattedMessage id="BackToSearch" defaultMessage="Back to search" />;
	};
	
	const handleBackClick = () => {
		const destination =
			backRedirectUri && decodeURIComponent(backRedirectUri).includes("/set/public")
				? "results"
				: "search";
		
		window.dataLayer.push({
			event: `cl_my_bookmarks_back_to_${destination}_clicked`,
		});
		
		if (backRedirectUri) {
			navigate(backRedirectUri);
		} else {
			navigate(`/?${takeLastSearchFilter}=${true}`);
		}
	};
	
	const handleSelectedItemSet = (item: ConDesignItemLite) => {
		setSelectedItem(item);
		if (!isAuthenticated) {
			setShowSigninModal(true);
		}
	};
	
	const handleRefetchData = () => {
		if (isAuthenticated) {
			setPaginationFrom(paginationFrom + designItemsLite.pageSize);
			setIsLoadingShowMore(true);
		} else {
			setShowSigninModal(true);
		}
	};
	
	const handleLoadMoreBookmarks = async () => {
		handleRefetchData();
		window.dataLayer.push({
			event: "cl_load_more_bookmarks",
		});
	};
	
	const handleCloseModal = () => {
		setShowSigninModal(false);
		setSelectedItem(null);
	};
	
	// Check if we should show empty state
	const showEmptyState =
		!isFetchingDesignItemsLite &&
		designItemsLite.totalItems === 0 &&
		designItemsLite.designItems.length === 0;
	
	return (
		<div className="bookmarks-page">
			{showSigninModal && (
				<SigninModal item={selectedItem} handleCloseModal={handleCloseModal} />
			)}
			
			<div className="bookmarks-breadcrumbs">
				<button className="bookmarks-breadcrumbs-button" onClick={handleBackClick}>
					<ChevronLeft className="chevron-left" />
					{getBreadcrumbText()}
				</button>
			</div>
			
			<div className="bookmarks-content-layout">
				<Filters
					featureFilters={featureFilters}
					connectorFilters={connectorFilters}
					popularityFilters={popularityFilters}
					designCodeFilter={designCodeFilter}
					setFeatureFilters={setFeatureFilters}
					setConnectorFilters={setConnectorFilters}
					setPopularityFilters={setPopularityFilters}
					setDesignCodeFilter={setDesignCodeFilter}
					handleApplyFilters={handleApplyFilters}
					handleApplyPopularityFilter={handleApplyPopularityFilter}
					handleSetOnlyFilter={handleSetOnlyFilter}
					clearAllFilters={clearAllFilters}
					resetDesignItems={resetBookmarksData}
					showPopularityFilters={false}
					showDesignCodeFilter={true}
				/>
				
				<div
					className={`bookmarks-items ${
						itemId ? "bookmarks-items-detail" : ""
					}`}
				>
					<p className="bookmarks-items-heading">
						<FormattedMessage id="MyBookmarks" defaultMessage="My bookmarks" />
					</p>
					
					{!showEmptyState &&
						!isFetchingDesignItemsLite &&
						designItemsLite.totalItems !== "filtering" && (
							<div className="bookmarks-items-total-count">
								{designItemsLite.totalItems}{" "}
								<FormattedMessage id="Results" defaultMessage="results" />
							</div>
						)}
					
					{/* Loading state */}
					{isFetchingDesignItemsLite && !isLoadingShowMore ? (
						itemId ? (
							<div className="loader-container loader-container-detail">
								<Loader />
							</div>
						) : (
							<div className="loader-container">
								<Loader />
							</div>
						)
					) : (
						<>
							{showEmptyState ? (
								<div className="bookmarks-empty-state">
									<div className="bookmarks-empty-message">
										<FormattedMessage
											id="NoBookmarksMessage"
											defaultMessage="No bookmarks here"
										/>
									</div>
									<div className="bookmarks-empty-submessage">
										<FormattedMessage
											id="NoBookmarksSubmessage"
											defaultMessage="Click the bookmark icon {bookmarkIcon} to add items to your bookmarks"
											values={{
												bookmarkIcon: <Bookmark className="bookmark-icon-inline" />,
											}}
										/>
									</div>
								</div>
							) : (
								<>
									{designItemsLite.totalItems ? (
										<CDIsContainer itemId={itemId}>
											{designItemsLite.designItems.map((item) => (
												<ConDesignItem
													key={item.id}
													conDesignItem={item}
													setSelectedItem={handleSelectedItemSet}
													isAuthenticated={true}
													redirectUri={`item/${item.id}${
														backRedirectUri
															? `?redirectUri=${encodeURIComponent(backRedirectUri)}`
															: ""
													}`}
													source="bookmarked"
												/>
											))}
										</CDIsContainer>
									) : null}
									
									{designItemsLite.isLastPage !== null &&
										designItemsLite.totalItems !== 0 &&
										designItemsLite.totalItems !== "filtering" &&
										!designItemsLite.isLastPage && (
											<ShowMoreButton
												itemId={itemId}
												onRefetchData={handleLoadMoreBookmarks}
												isLoading={isLoadingShowMore && isFetchingDesignItemsLite}
											/>
										)}
								</>
							)}
						</>
					)}
				</div>
				<Outlet />
			</div>
		</div>
	);
};

export default MyBookmarksPage;